import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import {
  FormProvider,
  NavProvider,
  Screen,
  useStateNavigator,
  useFormContext,
  SuperSchema,
  CustomComponentProps,
} from '@updater/ui-informant';
import {
  Alert,
  Box,
  CodeInput,
  ColorKey,
  Flex,
  Palette,
  Text,
  TextLink,
  Theme,
  UpdaterHeader,
} from '@updater/ui-uds';
import { ResponsiveValue } from 'styled-system';
import {
  ArrowsClockwise,
  DeviceMobile,
  Envelope,
  Folder,
  Password,
} from '@phosphor-icons/react';
import { OtpTypeEnum } from '@updater/consumer-graph/generated/graphql';
import { TrackEventInput } from '@updater/ui-tracker';
import { useTracking } from 'react-tracking';
import styled from '@emotion/styled';
import { AuthStackedTemplate } from './AuthStackedTemplate';
import { useAuth, useRESTClient } from 'context';
import { useFeature } from 'context/growthbook/useFeature';
import { useLoginStore } from 'flows/core/store';
import { EMAIL_REGX } from 'constants/regx';

import { useGenerateSigninOtp } from './hooks/useGenerateSigninOtp';
import { useSigninWithOtp } from './hooks/useSigninWithOtp';

const schema: SuperSchema = {
  type: 'object',
  required: ['email'],
  properties: {
    email: {
      title: 'email',
      type: 'string',
      pattern: EMAIL_REGX,
      ui: {
        label: 'Email',
        inputType: 'email',
        messages: {
          required: 'Please enter your email address',
          pattern: 'Please enter a valid email address',
        },
      },
    },
    password: {
      title: 'password',
      type: 'string',
      ui: {
        label: 'Password',
        component: 'security',
        messages: {
          required: 'Please enter a password',
        },
      },
    },
    otpCode: {
      title: 'otpCode',
      type: 'string',
      minLength: 6,
      maxLength: 6,
      ui: {
        component: 'OtpCodeInput',
        messages: {
          required: 'Please enter the code we sent you',
          minLength: 'Please enter all 6 digits',
        },
      },
    },
    emailDisplay: {
      title: 'emailDisplay',
      type: 'string',
      ui: {
        component: 'EmailDisplay',
      },
    },
  },
};

type FormData = {
  email: string;
  password: string;
  otpCode: string;
};

const OTP_LENGTH = 6;
const EMAIL_RESEND_COOLDOWN_SECONDS = 60;
const SMS_RESEND_COOLDOWN_SECONDS = 30;

const getResendCooldownSeconds = (type: OtpTypeEnum) =>
  type === OtpTypeEnum.SMS
    ? SMS_RESEND_COOLDOWN_SECONDS
    : EMAIL_RESEND_COOLDOWN_SECONDS;

const OtpCodeInput = ({ onChange, error, value }: CustomComponentProps) => {
  const { submitForm } = useFormContext();

  // CodeInput tracks its own digits internally and never reads `value` after
  // mount, so clearing the field alone (e.g. on resend) wouldn't clear the
  // visible boxes. Remounting via `key` whenever the field is reset to empty
  // forces it to start fresh.
  const [resetKey, setResetKey] = useState(0);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    if (!value) setResetKey((key) => key + 1);
  }

  const handleChange = async (code: string) => {
    await onChange(code);
    if (code.length === OTP_LENGTH) {
      await submitForm();
    }
  };

  return (
    <CodeInput
      key={resetKey}
      length={OTP_LENGTH}
      error={error}
      onChange={handleChange}
    />
  );
};

const EmailDisplayWrapper = styled(Flex)`
  height: 64px;
  padding: 0 ${({ theme }) => theme.space.s}px;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.input.disabledColor};
  border-radius: ${({ theme }) => theme.space.xs}px;
  background-color: ${({ theme }) => theme.input.backgroundColor};
`;

const EmailDisplay = () => {
  const { values } = useFormContext<FormData>();

  return (
    <EmailDisplayWrapper>
      <Text variant="m" color="textSoft">
        {values.email}
      </Text>
      <Envelope size={20} color={Palette.gray800} />
    </EmailDisplayWrapper>
  );
};

const StyledTextLink = styled(TextLink)`
  color: ${({ theme }) => theme.colors.cta};
`;

const StaticColorTextLink = styled(TextLink, {
  shouldForwardProp: (prop) => prop !== 'isCooldownActive',
})<{ isCooldownActive: boolean }>`
  &:hover {
    color: ${({ isCooldownActive }) =>
      isCooldownActive ? Palette.gray800 : Palette.blue600} !important;
  }
`;

const AuthCard = styled(Flex)`
  > form {
    width: 100%;
  }
`;

const PasswordResetter = forwardRef<{ reset: () => void }>((_, ref) => {
  const { setFieldValue } = useFormContext();
  useImperativeHandle(
    ref,
    () => ({ reset: () => setFieldValue('password', '') }),
    [setFieldValue]
  );
  return null;
});

const OtpCodeResetter = forwardRef<{ reset: () => void }>((_, ref) => {
  const { setFieldValue } = useFormContext();
  useImperativeHandle(
    ref,
    () => ({ reset: () => setFieldValue('otpCode', '') }),
    [setFieldValue]
  );
  return null;
});

type FieldErrorHandle = { setError: (message: string | undefined) => void };

const FieldErrorSetter = forwardRef<FieldErrorHandle, { name: string }>(
  ({ name }, ref) => {
    const { setFieldError } = useFormContext();
    useImperativeHandle(
      ref,
      () => ({
        setError: (message: string | undefined) => setFieldError(name, message),
      }),
      [setFieldError, name]
    );
    return null;
  }
);

interface Props {
  title?: string;
}

export const AuthOverlayOtp = ({ title }: Props) => {
  const { login, error: loginError, setSession } = useAuth();
  const { axios } = useRESTClient();
  const router = useRouter();
  const { email } = useLoginStore();
  const navigator = useStateNavigator('');
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'auth',
  });

  // Password, email OTP, and SMS OTP are always presented as available
  // options, regardless of what's actually configured on the account —
  // we don't query the server to find out, so as not to leak that info.
  const hasPassword = true;
  const setPasswordRef = useRef<{ reset: () => void } | null>(null);
  const setOtpCodeRef = useRef<{ reset: () => void } | null>(null);
  const setPasswordErrorRef = useRef<FieldErrorHandle | null>(null);
  const setOtpCodeErrorRef = useRef<FieldErrorHandle | null>(null);
  const { value: smsEnabled, loading: smsEnabledLoading } = useFeature(
    'consumer_otp_sms_enabled'
  );
  const effectiveSmsEnabled = smsEnabled || smsEnabledLoading;
  const [otpType, setOtpType] = useState<OtpTypeEnum>(OtpTypeEnum.SMS);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState<
    string | undefined
  >();
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);

  const navigationOverride = useRef<string | null>(null);
  const wrappedNavigator = useMemo(
    () => ({
      navigate: (path: string) => {
        if (navigationOverride.current !== null) {
          const override = navigationOverride.current;
          navigationOverride.current = null;
          return navigator.navigate(override, { replace: false });
        }
        return navigator.navigate(path, { replace: false });
      },
      getCurrentPath: () => navigator.getCurrentPath(),
    }),
    [navigator]
  );

  const [submittedEmail, setSubmittedEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  const generateOtp = useGenerateSigninOtp();
  const signinOtp = useSigninWithOtp();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (loginError) {
      setPasswordErrorRef.current?.setError(loginError.message);
    }
  }, [loginError]);

  useEffect(() => {
    setOtpCodeErrorRef.current?.setError(
      generateOtp.error ?? signinOtp.error ?? undefined
    );
  }, [generateOtp.error, signinOtp.error]);

  const sendOtp = async (targetEmail: string, targetType: OtpTypeEnum) => {
    signinOtp.clearError();
    const success = await generateOtp.resend(targetEmail, targetType);
    if (success) setResendCooldown(getResendCooldownSeconds(targetType));
  };

  const handleResendOtp = async () => {
    if (!submittedEmail || resendCooldown > 0) return;
    signinOtp.clearError();
    setOtpCodeRef.current?.reset();
    const success = await generateOtp.resend(submittedEmail, otpType);
    if (success) setResendCooldown(getResendCooldownSeconds(otpType));
  };

  const handleSwitchOtpType = async () => {
    if (!submittedEmail || !effectiveSmsEnabled) return;
    const newType =
      otpType === OtpTypeEnum.SMS ? OtpTypeEnum.EMAIL : OtpTypeEnum.SMS;

    setOtpType(newType);
    signinOtp.clearError();
    setOtpCodeRef.current?.reset();
    const success = await generateOtp.resend(submittedEmail, newType);
    if (success) setResendCooldown(getResendCooldownSeconds(newType));
  };

  const handleTryPassword = () => {
    generateOtp.reset();
    signinOtp.clearError();
    setResendCooldown(0);
    setPasswordRef.current?.reset();
    setPasswordErrorRef.current?.setError(undefined);
    wrappedNavigator.navigate('/password');
  };

  const handleForgotPassword = async () => {
    if (!submittedEmail) return;
    setForgotPasswordLoading(true);
    setForgotPasswordError(undefined);
    try {
      await axios.post('v2/password_resets', { email: submittedEmail });
      setForgotPasswordSent(true);
    } catch {
      setForgotPasswordError(
        "We couldn't send the reset email. Please try again."
      );
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleSubmit = async (values: FormData) => {
    const result = await signinOtp.signin(
      values.email,
      values.otpCode,
      otpType
    );
    if (!result) return;
    setSession({
      userId: result.mover.id,
      userUUID: result.mover.uuid,
      session: {
        accessToken: result.session.accessToken,
        client: result.session.client,
        uid: result.session.uid,
      },
    });
    trackEvent({
      object: 'passwordless-login',
      verb: 'submitted',
      details: {
        loginMethod: otpType === OtpTypeEnum.SMS ? 'sms' : 'email',
        hasPassword,
      },
    });
  };

  const otherOtpType =
    otpType === OtpTypeEnum.SMS ? OtpTypeEnum.EMAIL : OtpTypeEnum.SMS;
  const switchOtpTypeLabel =
    otherOtpType === OtpTypeEnum.SMS ? 'Text me a code' : 'Send via email';
  const isResendGray = resendCooldown > 0 || generateOtp.resendLoading;

  return (
    <Flex
      flexDirection="column"
      minHeight="100vh"
      backgroundColor={Palette.blue950 as ResponsiveValue<ColorKey, Theme>}
    >
      <UpdaterHeader
        data-testid="updater-header"
        onLogoClick={() => router.push('/')}
        menuRequired={false}
        headerBgColor={Palette.blue950}
        logoColor="white"
      />
      <Flex
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        px="s"
      >
        <AuthCard
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
          maxWidth="490px"
          p="l"
          gap="m"
          backgroundColor="white"
          borderRadius="s"
          boxShadow="0 0 0 1px rgba(40, 41, 60, 0.08), 0 1px 0 0 rgba(40, 41, 60, 0.06), 0 2px 4px 0 rgba(40, 41, 60, 0.04)"
        >
          <FormProvider
            schema={schema}
            initialValues={{
              email: email ?? '',
              password: '',
              otpCode: '',
            }}
            onSubmit={handleSubmit}
            customComponents={{
              OtpCodeInput,
              EmailDisplay,
            }}
          >
            <PasswordResetter ref={setPasswordRef} />
            <OtpCodeResetter ref={setOtpCodeRef} />
            <FieldErrorSetter name="password" ref={setPasswordErrorRef} />
            <FieldErrorSetter name="otpCode" ref={setOtpCodeErrorRef} />
            <NavProvider
              template={AuthStackedTemplate}
              customLatestScreenIdx={2}
              onBeforeNavigate={async ({
                fromScreen,
                fromIndex,
                toIndex,
                formValues,
              }) => {
                // email screen forward
                if (fromScreen?.name === 'emailScreen' && toIndex > fromIndex) {
                  setSubmittedEmail(formValues.email);
                  setForgotPasswordSent(false);
                  setForgotPasswordError(undefined);
                  setPasswordErrorRef.current?.setError(undefined);
                  setPasswordRef.current?.reset();
                  const defaultOtpType = effectiveSmsEnabled
                    ? OtpTypeEnum.SMS
                    : OtpTypeEnum.EMAIL;
                  setOtpType(defaultOtpType);

                  navigationOverride.current = '/verify';
                  void sendOtp(formValues.email, defaultOtpType);
                  return;
                }

                // otp screen back
                if (fromScreen?.name === 'otpScreen' && toIndex < fromIndex) {
                  generateOtp.reset();
                  signinOtp.clearError();
                  setResendCooldown(0);
                  navigationOverride.current = '/';
                  return;
                }

                // password screen back
                if (
                  fromScreen?.name === 'passwordScreen' &&
                  toIndex < fromIndex
                ) {
                  navigationOverride.current = '/verify';
                  return;
                }

                // password screen forward
                if (
                  fromScreen?.name === 'passwordScreen' &&
                  toIndex > fromIndex
                ) {
                  navigationOverride.current = '/password';
                  await login(formValues.email, formValues.password);
                  trackEvent({
                    object: 'passwordless-login',
                    verb: 'submitted',
                    details: { loginMethod: 'password', hasPassword },
                  });
                  return;
                }
              }}
              navigator={wrappedNavigator}
            >
              <Screen
                name="emailScreen"
                heading={title ?? 'Welcome back!'}
                subHeading="Enter your email to continue."
                route=""
                fields={['email']}
                hideBackButton
              />
              <Screen
                name="passwordScreen"
                heading="Enter your password"
                route="password"
                fields={['password']}
                nextButtonText="Log In"
                templateProps={{
                  footerPanel: (
                    <Box>
                      {forgotPasswordSent ? (
                        <Box>
                          <Alert
                            variant="success"
                            message="Please check your email for a password reset link if your account is valid."
                            mt="s"
                          />
                          <TextLink
                            data-testid="resend-forgot-password"
                            variant="m"
                            mt="s"
                            onClick={handleForgotPassword}
                          >
                            {forgotPasswordLoading
                              ? 'Sending…'
                              : 'Resend email'}
                          </TextLink>
                        </Box>
                      ) : (
                        <StyledTextLink
                          data-testid="forgot-password"
                          variant="m"
                          onClick={handleForgotPassword}
                        >
                          {forgotPasswordLoading
                            ? 'Sending…'
                            : 'Forgot Password?'}
                        </StyledTextLink>
                      )}
                      {forgotPasswordError && (
                        <Alert
                          message={forgotPasswordError}
                          variant="error"
                          mt="s"
                        />
                      )}
                    </Box>
                  ),
                }}
              />
              <Screen
                name="otpScreen"
                heading={
                  otpType === OtpTypeEnum.SMS
                    ? 'Check your phone'
                    : 'Check your email inbox'
                }
                subHeading="If this account exists, you’ll receive a passcode."
                route="verify"
                fields={
                  otpType === OtpTypeEnum.EMAIL
                    ? ['emailDisplay', 'otpCode']
                    : ['otpCode']
                }
                hideNextButton
                templateProps={{
                  footerPanel: (
                    <Flex
                      flexDirection="column"
                      alignItems="flex-start"
                      alignSelf="stretch"
                      p="s"
                      borderRadius="lg"
                      backgroundColor={
                        Palette.blue50 as ResponsiveValue<ColorKey, Theme>
                      }
                      style={{ gap: '12px' }}
                    >
                      <Text
                        variant="mBold"
                        style={{ fontFeatureSettings: "'salt' on" }}
                      >
                        Didn&apos;t get the code?
                      </Text>
                      {otpType === OtpTypeEnum.EMAIL && (
                        <Flex as="span" alignItems="center" gap="xs">
                          <Folder size={16} color={Palette.gray800} />
                          <Text
                            variant="m"
                            color="textSoft"
                            style={{ fontFeatureSettings: "'salt' on" }}
                          >
                            Check your spam folder.
                          </Text>
                        </Flex>
                      )}
                      {effectiveSmsEnabled && (
                        <StaticColorTextLink
                          data-testid="switch-otp-type"
                          variant="m"
                          isCooldownActive={false}
                          color="textSoft"
                          onClick={handleSwitchOtpType}
                          style={{
                            fontFeatureSettings: "'salt' on",
                            cursor: 'pointer',
                          }}
                        >
                          <Flex as="span" alignItems="center" gap="xs">
                            {otherOtpType === OtpTypeEnum.SMS ? (
                              <DeviceMobile size={16} color={Palette.blue600} />
                            ) : (
                              <Envelope size={16} color={Palette.blue600} />
                            )}
                            <span>{switchOtpTypeLabel}</span>
                          </Flex>
                        </StaticColorTextLink>
                      )}
                      <StaticColorTextLink
                        data-testid="resend-otp"
                        variant="m"
                        isCooldownActive={isResendGray}
                        color={isResendGray ? Palette.gray800 : 'textSoft'}
                        onClick={
                          resendCooldown === 0 ? handleResendOtp : undefined
                        }
                        style={{
                          fontFeatureSettings: "'salt' on",
                          cursor: resendCooldown > 0 ? 'default' : 'pointer',
                        }}
                      >
                        <Flex as="span" alignItems="center" gap="xs">
                          <Password
                            size={16}
                            color={
                              isResendGray ? Palette.gray800 : Palette.blue600
                            }
                          />
                          <span>
                            {generateOtp.resendLoading
                              ? 'Sending…'
                              : resendCooldown > 0
                              ? `Send a new code in ${resendCooldown}s`
                              : 'Send a new code'}
                          </span>
                        </Flex>
                      </StaticColorTextLink>
                      <TextLink
                        data-testid="try-password"
                        variant="m"
                        color="textSoft"
                        onClick={handleTryPassword}
                        style={{
                          fontFeatureSettings: "'salt' on",
                          cursor: 'pointer',
                        }}
                      >
                        <Flex as="span" alignItems="center" gap="xs">
                          <ArrowsClockwise size={16} color={Palette.blue600} />
                          <span>Try entering password</span>
                        </Flex>
                      </TextLink>
                    </Flex>
                  ),
                }}
              />
            </NavProvider>
          </FormProvider>
        </AuthCard>
      </Flex>
    </Flex>
  );
};
